import React from 'react';
import { Profile } from '@/types/auth';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Trophy, Award } from 'lucide-react';

interface AchievementsWidgetProps {
  profile: Profile;
}

export const AchievementsWidget: React.FC<AchievementsWidgetProps> = ({ profile }) => {
  const { data: badges, isLoading } = useQuery({
    queryKey: ['user-badges', profile.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_badges')
        .select(`
          *,
          badges (
            name,
            description,
            image_url,
            type
          )
        `)
        .eq('user_id', profile.id)
        .order('earned_at', { ascending: false })
        .limit(6);

      if (error) throw error;
      return data;
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!badges || badges.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        <div className="text-center">
          <Trophy className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No badges earned yet</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full">
      <h3 className="text-sm font-medium text-muted-foreground mb-4">Achievements</h3>
      
      <div className="grid grid-cols-3 gap-3 h-[calc(100%-2rem)]">
        {badges.slice(0, 6).map((userBadge) => (
          <div
            key={userBadge.id}
            className="flex flex-col items-center text-center group"
          >
            <div className="relative">
              {userBadge.badges?.image_url ? (
                <img
                  src={userBadge.badges.image_url}
                  alt={userBadge.badges?.name}
                  className="w-8 h-8 rounded-full group-hover:scale-110 transition-transform"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Award className="h-4 w-4 text-white" />
                </div>
              )}
              
              {userBadge.earning_order <= 3 && (
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-primary rounded-full flex items-center justify-center text-xs font-bold text-primary-foreground">
                  {userBadge.earning_order}
                </div>
              )}
            </div>
            
            <p className="text-xs font-medium mt-2 truncate w-full">
              {userBadge.badges?.name}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};